import api from './api';

export interface ModulePermission {
  accessType: string;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canView: boolean;
}

export const MODULE_NAMES = [
  'Administration',
  'MAVIS-Pay',
  'MAVIS-HR',
  'MAVIS-TMS',
  'MAVIS-Leave',
  'MAVIS-Claim',
  'MAVIS-Appraisal',
] as const;

export const myAccessService = {
  getMyAccess: () => api.get('/admin/my-access'),
};

export const companyService = {
  list: () => api.get('/admin/companies'),
  create: (data: { code: string; name: string; uen?: string; address?: string }) => api.post('/admin/companies', data),
  update: (id: string, data: { name: string; uen?: string; address?: string; active?: boolean }) =>
    api.put(`/admin/companies/${id}`, data),
  uploadLogo: (id: string, file: File, kind: 'mobile' | 'payslip') => {
    const form = new FormData();
    form.append('logo', file);
    form.append('kind', kind);
    return api.post(`/admin/companies/${id}/logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export interface NodePermission {
  nodeKey: string;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canView: boolean;
}

export const accessRightService = {
  list: () => api.get('/admin/access-rights'),
  create: (data: any) => api.post('/admin/access-rights', data),
  update: (id: string, data: any) => api.put(`/admin/access-rights/${id}`, data),
  remove: (id: string) => api.delete(`/admin/access-rights/${id}`),
  // ADM-07b: per-node overrides. Not called by any page yet — ADM-07c wires these
  // into the Access Rights tree UI.
  getNodePermissions: (id: string) => api.get(`/admin/access-rights/${id}/node-permissions`),
  updateNodePermissions: (id: string, permissions: NodePermission[]) =>
    api.put(`/admin/access-rights/${id}/node-permissions`, { permissions }),
};

export const userService = {
  list: (active: boolean) => api.get('/admin/users', { params: { active } }),
  get: (id: string) => api.get(`/admin/users/${id}`),
  create: (data: any) => api.post('/admin/users', data),
  update: (id: string, data: any) => api.put(`/admin/users/${id}`, data),
  deactivate: (id: string) => api.post(`/admin/users/${id}/deactivate`),
  reactivate: (id: string) => api.post(`/admin/users/${id}/reactivate`),
  // Omit newPassword to have the backend generate a temporary one. Either way
  // the user is forced to choose their own password at their next login.
  resetPassword: (id: string, newPassword?: string) =>
    api.post(`/admin/users/${id}/reset-password`, { newPassword }),
  bulkResetPassword: (params: { userIds?: string[]; allActive?: boolean; newPassword?: string }) =>
    api.post('/admin/users/bulk-reset-password', params),
  set2fa: (id: string, enabled: boolean) => api.put(`/admin/users/${id}/2fa`, { enabled }),
  lookupEmployees: (search?: string, excludeExistingUsers?: boolean) =>
    api.get('/admin/users/lookup/employees', { params: { search, excludeExistingUsers } }),
};

export const employeeAccessService = {
  list: (resigned?: boolean, search?: string) =>
    api.get('/admin/employee-access', { params: { resigned, search } }),
  save: (employeeId: string, modules: Record<string, boolean>) =>
    api.post('/admin/employee-access', { employeeId, modules }),
  bulkResetPassword: (employeeIds: string[], newPassword: string) =>
    api.post('/admin/employee-access/bulk-reset-password', { employeeIds, newPassword }),
  resetPassword: (employeeId: string) => api.post(`/admin/employee-access/${employeeId}/reset-password`),
  resendLogin: (employeeId: string) => api.post(`/admin/employee-access/${employeeId}/resend-login`),
  sendWelcomeEmail: (employeeId: string) => api.post(`/admin/employee-access/${employeeId}/send-welcome-email`),
};

export const approverSettingsService = {
  list: (moduleName?: string) => api.get('/admin/approver-settings', { params: { moduleName } }),
  candidates: (moduleName?: string, employeeId?: string) =>
    api.get('/admin/approver-settings/candidates', { params: { moduleName, employeeId } }),
  batchAssign: (data: any) => api.post('/admin/approver-settings/batch', data),
  replace: (data: { fromApproverId: string; toApproverId: string; moduleName?: string }) =>
    api.post('/admin/approver-settings/replace', data),
};

export const smtpSettingsService = {
  get: () => api.get('/admin/smtp-settings'),
  update: (data: any) => api.put('/admin/smtp-settings', data),
  testMail: (to: string) => api.post('/admin/smtp-settings/test-mail', { to }),
  getPasswordPolicy: () => api.get('/admin/password-policy'),
  updatePasswordPolicy: (data: { restrictReuseDays: number; forceChangeDays: number }) =>
    api.put('/admin/password-policy', data),
};

export const notificationService = {
  list: () => api.get('/admin/notifications'),
  markRead: (id: string) => api.post(`/admin/notifications/${id}/read`),
  markAllRead: () => api.post('/admin/notifications/mark-all-read'),
};
